// @flow

import R from 'ramda';

import type { Column, Columns } from '../../../main/types/tableTypes';
import { DRUID_TO_HUMAN_READABLE } from '../../../common/support/eo_mapping';

type ColumnHierarchy = {
  headerColumns: Columns,
  subColumns: Columns
};

export function getDefaultSelectedColumns(
  columns: any,
  selectedKeys: Array<string>
) {
  return selectedKeys
    .map(colKey => {
      return columns.find(R.propEq('key', colKey));
    })
    .filter(column => !!column);
}

export function getRowValuesFromColumnHelpers(
  columns: Columns = [],
  data: Array<Object> = []
): Array<Object> {
  return data.map(row => {
    return columns.reduce((acc, col) => {
      const val = col.getValue ? col.getValue(row) : row[col.key];
      return R.assoc(col.key, val, acc);
    }, row);
  });
}

export const addHeaderColumn = (
  hierarchy: ColumnHierarchy,
  column: Column,
  key: string
): ColumnHierarchy => {
  return R.assoc(
    'headerColumns',
    hierarchy.headerColumns.concat({
      key,
      display: DRUID_TO_HUMAN_READABLE[key] || key,
      subCols: [column],
      no_sort: true,
      type: column.type
    }),
    hierarchy
  );
};

export const updateHeaderColumn = (
  hierarchy: ColumnHierarchy,
  column: Column,
  key: string
): ColumnHierarchy => {
  const colHeaderInd = R.findIndex(
    R.propEq('key', key),
    hierarchy.headerColumns
  );
  const subColLens = R.lensPath(['headerColumns', colHeaderInd, 'subCols']);
  return R.set(
    subColLens,
    R.view(subColLens, hierarchy).concat(column),
    hierarchy
  );
};

export const addSubColumn = (
  hierarchy: ColumnHierarchy,
  column: Column
): ColumnHierarchy => {
  return R.assoc('subColumns', hierarchy.subColumns.concat(column), hierarchy);
};

export const generateColumnHierarchy = (columns: Columns): ColumnHierarchy => {
  return columns.reduce(
    (acc, column) => {
      // case: incoming column is a subcolumn
      if (R.has('group', column)) {
        const key = R.path(['group', 'key'], column);

        // create the header column and add the current column to it's subCols prop
        if (!R.find(R.propEq('key', key), acc.headerColumns)) {
          acc = addHeaderColumn(acc, column, key);
          return addSubColumn(acc, column);
        }
        // add column to acc.headerColumn.subCols
        acc = updateHeaderColumn(acc, column, key);
        // add column to acc.subColumns in either case
        return addSubColumn(acc, column);
      }
      return R.assoc('headerColumns', acc.headerColumns.concat(column), acc);
    },
    { headerColumns: [], subColumns: [] }
  );
};

export const getDataSourcesFromColumns = (selectedColumns: Columns) => {
  return [
    ...new Set(
      selectedColumns &&
        // $FlowFixMe
        selectedColumns.flatMap(column => {
          return column.dataSources;
        })
    )
  ].filter(column => !!column);
};

// ensures backwards compatibility of bookmarks for those created before the dynamic data loading table model
export const regenerateSelectedColumns = (
  selectedColumns: Columns,
  allColumns: Columns
) => {
  if (!selectedColumns) {
    return selectedColumns;
  }
  const dataSources = getDataSourcesFromColumns(selectedColumns);
  if (!dataSources.length) {
    return selectedColumns.map(column => {
      return allColumns.find(c => c.key === column.key) || column;
    });
  }
  return selectedColumns;
};
